import { useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, ImagePlus, Camera, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import MoyenSelector from "@/components/MoyenSelector";
import api, { resolveFileUrl } from "@/lib/api";

export default function StepEditor({ step, index, moyensList, onChange, onRemove }) {
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const updateField = (field, val) => {
    onChange({ ...step, [field]: val });
  };

  const handleMoyenSelect = (slotIdx, moyenData) => {
    const moyens = [...step.moyens];
    moyens[slotIdx] = {
      moyen: moyenData.moyen,
      risques: moyenData.risques || "",
      mesures_prevention: moyenData.mesures_prevention || [],
      habilitation: moyenData.habilitation || "",
      permis: moyenData.permis || [],
      surveillance_coordination: moyenData.surveillance_coordination || "",
      formation: moyenData.formation || "",
    };
    updateField("moyens", moyens);
  };

  const handleMoyenClear = (slotIdx) => {
    const moyens = [...step.moyens];
    moyens.splice(slotIdx, 1);
    updateField("moyens", moyens);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateField("photos", [...(step.photos || []), res.data.url]);
      toast.success("Photo ajoutée");
    } catch (err) {
      toast.error("Erreur lors de l'upload de la photo");
    } finally {
      setUploading(false);
      if (galleryInputRef.current) galleryInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const removePhoto = (photoIdx) => {
    const photos = [...(step.photos || [])];
    photos.splice(photoIdx, 1);
    updateField("photos", photos);
  };

  const slots = [0, 1, 2];

  return (
    <div className="border border-border rounded-lg p-4 bg-card space-y-4" data-testid={`step-card-${index}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-heading font-semibold text-foreground">Étape {index + 1}</h4>
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          data-testid={`remove-step-button-${index}`}
          title="Supprimer cette étape"
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          Description de l'opération
        </Label>
        <Textarea
          value={step.description}
          onChange={(e) => updateField("description", e.target.value)}
          placeholder="Décrire l'étape par étape..."
          className="mt-1"
          data-testid={`step-description-input-${index}`}
        />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Photos / Vidéos</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {(step.photos || []).map((url, pIdx) => (
            <div key={pIdx} className="relative h-20 w-20 rounded-md overflow-hidden border border-border">
              <img src={resolveFileUrl(url)} alt={`Photo étape ${index + 1}`} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(pIdx)}
                className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"
                data-testid={`remove-photo-button-${index}-${pIdx}`}
              >
                <Trash2 className="h-3 w-3 text-white" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            className="h-20 w-20 rounded-md border border-dashed border-border flex flex-col items-center justify-center gap-1 hover:bg-accent/50"
            data-testid={`take-photo-button-${index}`}
            title="Prendre une photo"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <>
                <Camera className="h-5 w-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Appareil photo</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => galleryInputRef.current?.click()}
            className="h-20 w-20 rounded-md border border-dashed border-border flex flex-col items-center justify-center gap-1 hover:bg-accent/50"
            data-testid={`upload-photo-button-${index}`}
            title="Choisir depuis la galerie"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <>
                <ImagePlus className="h-5 w-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Galerie</span>
              </>
            )}
          </button>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            className="hidden"
            onChange={handleFileUpload}
            data-testid={`camera-file-input-${index}`}
          />
          <input
            type="file"
            accept="image/*"
            ref={galleryInputRef}
            className="hidden"
            onChange={handleFileUpload}
            data-testid={`photo-file-input-${index}`}
          />
        </div>
      </div>

      <div className="space-y-3">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          Moyens utilisés (1 à 3 maximum)
        </Label>
        {slots.map((slotIdx) => {
          const m = step.moyens[slotIdx];
          if (slotIdx > 0 && !step.moyens[slotIdx - 1]) return null;
          return (
            <div key={slotIdx} className="space-y-2">
              <MoyenSelector
                moyensList={moyensList}
                value={m?.moyen}
                onSelect={(data) => handleMoyenSelect(slotIdx, data)}
                onClear={() => handleMoyenClear(slotIdx)}
                testId={`moyen-selector-${index}-${slotIdx}`}
              />
              {m && (
                <div className="bg-muted/50 border border-border rounded-md p-3 text-xs space-y-1" data-testid={`moyen-detail-${index}-${slotIdx}`}>
                  {m.risques && (
                    <p>
                      <span className="font-semibold text-destructive">Risques : </span>
                      {m.risques}
                    </p>
                  )}
                  {m.mesures_prevention?.length > 0 && (
                    <p>
                      <span className="font-semibold text-success">Mesures de prévention : </span>
                      {m.mesures_prevention.join(" — ")}
                    </p>
                  )}
                  {m.habilitation && (
                    <p>
                      <span className="font-semibold">Habilitation : </span>
                      {m.habilitation}
                    </p>
                  )}
                  {m.permis?.length > 0 && (
                    <p>
                      <span className="font-semibold">Permis : </span>
                      {m.permis.join(", ")}
                    </p>
                  )}
                  {m.surveillance_coordination && (
                    <p>
                      <span className="font-semibold">Surveillance/Coordination : </span>
                      {m.surveillance_coordination}
                    </p>
                  )}
                  {m.formation && (
                    <p>
                      <span className="font-semibold">Formation : </span>
                      {m.formation}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
